import React, { useEffect, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import styles from "./DBListPager.module.scss";
import { useLocation } from "react-router-dom";
import SonicLink from "../routing/SonicLink";

type Props = {
  totalPage: number;
  currentPage: number;
  shouldScrollToTop?: boolean;
};

const DBListPager: React.FC<Props> = ({
  totalPage,
  currentPage,
  shouldScrollToTop,
}) => {
  currentPage = currentPage < 1 ? 1 : currentPage;

  const location = useLocation();
  const base = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete("pg");

    const path = location.pathname.endsWith("/")
      ? location.pathname
      : location.pathname + "/";
    const search = searchParams.toString().length
      ? "?" + searchParams.toString()
      : undefined;

    return {
      link: search ? path + search : path,
      pgKey: search ? "&pg=" : "?pg=",
    };
  }, [location.search]);

  const LEFT_PAGE = "LEFT";
  const RIGHT_PAGE = "RIGHT";

  const range = (from: number, to: number, step = 1): (number | string)[] => {
    let i = from;
    const range = [];

    while (i <= to) {
      if (typeof i === "string") {
        continue;
      } else {
        range.push(i);
        i += step;
      }
    }

    return range;
  };

  const fetchPageNumbers = () => {
    const pageNeighbours = 1;

    /**
     * totalNumbers: the total page numbers to show on the control
     * totalBlocks: totalNumbers + 2 to cover for the left(<) and right(>) controls
     */
    const totalNumbers = pageNeighbours * 2 + 3;
    const totalBlocks = totalNumbers + 2;

    if (totalPage > totalBlocks) {
      const startPage = Math.max(2, currentPage - pageNeighbours);
      const endPage = Math.min(totalPage - 1, currentPage + pageNeighbours);
      let pages = range(startPage, endPage);

      /**
       * hasLeftSpill: has hidden pages to the left
       * hasRightSpill: has hidden pages to the right
       * spillOffset: number of hidden pages either to the left or to the right
       */
      const hasLeftSpill = startPage > 2;
      const hasRightSpill = totalPage - endPage > 1;
      const spillOffset = totalNumbers - (pages.length + 2);

      switch (true) {
        // handle: (1) < {5 6} [7] {8 9} (10)
        case hasLeftSpill && !hasRightSpill: {
          const extraPages = range(startPage - spillOffset, startPage - 1);
          pages = [1, "...", ...extraPages, ...pages, totalPage];
          break;
        }

        // handle: (1) {2 3} [4] {5 6} > (10)
        case !hasLeftSpill && hasRightSpill: {
          const extraPages = range(endPage + 1, endPage + spillOffset);
          pages = [1, ...pages, ...extraPages, "...", totalPage];
          break;
        }

        // handle: (1) < {4 5} [6] {7 8} > (10)
        case hasLeftSpill && hasRightSpill:
        default: {
          pages = [1, "...", ...pages, "...", totalPage];
          break;
        }
      }

      return [LEFT_PAGE, ...pages, RIGHT_PAGE];
    }

    return range(1, totalPage);
  };

  const pages = fetchPageNumbers();

  useEffect(() => {
    if (shouldScrollToTop) {
      window.scrollTo(0, 0);
    }
  }, [currentPage]);

  return (
    <nav aria-label="Countries Pagination">
      <ul className={styles["g-dbListPager"]}>
        {pages.map((page, index) => {
          if (page === LEFT_PAGE)
            return (
              <li
                key={index}
                className={`${styles["g-dbListPager__item"]} ${
                  currentPage === 1 ? `${styles["disabled"]}` : ""
                }`}
              >
                <SonicLink
                  to={
                    currentPage === 2
                      ? base.link
                      : `${base.link}${base.pgKey}${currentPage - 1}`
                  }
                  className={styles["g-dbListPager__item-link"]}
                  disabled={currentPage === 1}
                  ariaLabel="Previous"
                >
                  <span aria-hidden="true">
                    <FontAwesomeIcon icon={["fal", "chevron-left"]} />
                  </span>
                </SonicLink>
              </li>
            );

          if (page === RIGHT_PAGE)
            return (
              <li
                key={index}
                className={`${styles["g-dbListPager__item"]} ${
                  currentPage === totalPage ? `${styles["disabled"]}` : ""
                }`}
              >
                <SonicLink
                  to={`${base.link}${base.pgKey}${
                    currentPage < totalPage ? currentPage + 1 : totalPage
                  }`}
                  className={styles["g-dbListPager__item-link"]}
                  disabled={currentPage === totalPage}
                  ariaLabel="Next"
                >
                  <span aria-hidden="true">
                    <FontAwesomeIcon icon={["fal", "chevron-right"]} />
                  </span>
                </SonicLink>
              </li>
            );

          return (
            <li
              key={index}
              className={`${styles["g-dbListPager__item"]}
              ${currentPage === page ? `${styles["active"]}` : ""}
              ${typeof page === "string" ? `${styles["dot"]}` : ""}
              `}
            >
              {typeof page === "string" ? (
                <span>{page}</span>
              ) : page === currentPage ? (
                <span className={styles["g-dbListPager__item-link"]}>
                  {page}
                </span>
              ) : (
                <SonicLink
                  to={page === 1 ? base.link : base.link + base.pgKey + page}
                  className={styles["g-dbListPager__item-link"]}
                >
                  {page}
                </SonicLink>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default DBListPager;
